<template>
  <!-- Detail-page action bar (S&R job, Replacement Quote, Universal Quote).
       Rule: every visible action goes in ONE ordered list; the first `maxPills` (3; 1 below laptop) are pills,
       the rest live in the ⋮ menu (no ⋮ when there is nothing left over).
       Order: primary action first (first `primary: true`, else the first non-destructive one),
       then the remaining normal actions, then destructive ones (variant 'danger') — relative order kept. -->
  <div class="flex flex-wrap justify-end items-center gap-2">
    <template v-for="action in pillActions" :key="action.key">
      <slot name="pill" :action="action" :run="run" :narrow="narrow">
        <button type="button" :disabled="action.disabled"
          class="group flex items-center rounded-full gap-2 py-1.5 font-roboto font-normal text-[15px] largeDesktop:text-[17px] h-[40px] border-1 whitespace-nowrap transition duration-300 disabled:cursor-not-allowed disabled:opacity-[0.5]"
          :class="[action.originalImg ? 'pl-3 pr-2' : 'px-4', pillClass(action)]"
          :style="{ '--pill': colorOf(action) }" @click="run(action)">
          {{ action.label }}
          <span v-if="action.originalImg" class="w-[26px] h-[26px] rounded-full flex items-center justify-center">
            <!-- below laptop the pill is filled (touch has no hover): show the hover icon -->
            <img :src="action.originalImg" alt="" class="w-full h-full object-contain"
              :class="narrow ? 'hidden' : 'group-hover:hidden block'">
            <img :src="action.hoverImg || action.originalImg" alt=""
              class="w-full h-full object-contain" :class="narrow ? 'block' : 'group-hover:block hidden'">
          </span>
        </button>
      </slot>
    </template>

    <div v-if="menuActions.length" ref="root" class="relative">
      <button :id="triggerId" ref="trigger" type="button" aria-haspopup="menu" :aria-expanded="open ? 'true' : 'false'"
        :aria-controls="menuId" :aria-label="menuLabel" :class="triggerClass" @click="toggle"
        @keydown.down.prevent="openMenu(0)" @keydown.up.prevent="openMenu(-1)">
        <slot name="trigger" :open="open">
          <i class="fa fa-ellipsis-v" aria-hidden="true" />
        </slot>
      </button>
      <div v-if="open" :class="menuClass" :style="menuStyle" @keydown="onMenuKeydown">
        <slot name="menu-decor" />
        <ul :id="menuId" role="menu" :aria-labelledby="triggerId" :class="listClass">
          <li v-for="(action, i) in menuActions" :key="'m-' + action.key" role="none" :class="liClass">
            <button :ref="el => setItemRef(el, i)" type="button" role="menuitem" tabindex="-1"
              :disabled="action.disabled" :aria-disabled="action.disabled ? 'true' : undefined" :class="itemClass"
              :style="action.variant === 'danger' ? { color: dangerColor } : {}" @click="run(action)">
              {{ action.label }}
            </button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
let uid = 0;

export default {
  name: "ActionBar",
  props: {
    // [{ key, label, onClick, color?, originalImg?, hoverImg?, variant?: 'danger', primary?, disabled? }]
    actions: { type: Array, default: () => [] },
    maxPills: { type: Number, default: 3 },
    menuLabel: { type: String, default: "More actions" },
    dangerColor: { type: String, default: "#D7385E" },
    defaultColor: { type: String, default: "#4E7BFF" },
    triggerClass: {
      type: [String, Array, Object],
      // below laptop (touch, no hover) the ⋮ is filled like the pills
      default: "w-[40px] h-[40px] rounded-full flex items-center justify-center bg-[#fff] border-1 border-[#00B7C2] text-[#00B7C2] [@media(max-width:1023px)]:!bg-[#00B7C2] [@media(max-width:1023px)]:!text-white",
    },
    menuClass: {
      type: [String, Array, Object],
      // pressed item: light purple, not Bootstrap's blue (#0d6efd) with white text
      default: "dropdown-menu show p-1 rounded-[10px] shadow ![--bs-dropdown-link-active-bg:#EEEAFB] ![--bs-dropdown-link-active-color:#333333]",
    },
    // Bootstrap's .dropdown-menu is absolutely positioned; without Popper it needs explicit offsets
    menuStyle: {
      type: Object,
      default: () => ({ top: "calc(100% + 4px)", right: 0, left: "auto" }),
    },
    listClass: { type: [String, Array, Object], default: "list-none m-0 p-0" },
    liClass: { type: [String, Array, Object], default: "" },
    itemClass: {
      type: [String, Array, Object],
      default: "dropdown-item rounded-[6px] py-2 whitespace-nowrap disabled:opacity-[0.5] text-[14px] focus-visible:outline-none focus-visible:!bg-[#F3F0FF]",
    },
  },
  data() {
    uid += 1;
    return {
      open: false,
      itemRefs: [],
      // below laptop (1024px) only one pill fits; the rest go in the ⋮ menu
      narrow: false,
      triggerId: `action-bar-trigger-${uid}`,
      menuId: `action-bar-menu-${uid}`,
    };
  },
  computed: {
    ordered() {
      const list = (this.actions || []).filter(Boolean);
      const isDanger = a => a.variant === "danger";
      const primary = list.find(a => a.primary) || list.find(a => !isDanger(a)) || null;
      const rest = list.filter(a => a !== primary);
      return (primary ? [primary] : []).concat(rest.filter(a => !isDanger(a)), rest.filter(isDanger));
    },
    pillCount() {
      return this.narrow ? 1 : this.maxPills;
    },
    pillActions() {
      return this.ordered.slice(0, this.pillCount);
    },
    menuActions() {
      return this.ordered.slice(this.pillCount);
    },
  },
  mounted() {
    this.media = window.matchMedia("(max-width: 1023px)");
    this.onMedia = () => { this.narrow = this.media.matches; };
    this.onMedia();
    this.media.addEventListener("change", this.onMedia);
  },
  watch: {
    menuActions(list) {
      if (!list.length) this.close(false);
    },
  },
  beforeUpdate() {
    this.itemRefs = [];
  },
  beforeUnmount() {
    this.media?.removeEventListener("change", this.onMedia);
    this.removeListeners();
  },
  methods: {
    // filled below laptop (touch has no hover); outlined with a CSS :hover fill on laptop
    pillClass(action) {
      if (this.narrow) return "bg-[color:var(--pill)] border-[color:var(--pill)] text-[#fff]";
      return "bg-[#fff] border-[color:var(--pill)] text-[color:var(--pill)]" + (action.disabled ? "" : " hover:bg-[color:var(--pill)] hover:text-[#fff]");
    },
    colorOf(action) {
      return action.color || (action.variant === "danger" ? this.dangerColor : this.defaultColor);
    },
    setItemRef(el, i) {
      if (el) this.itemRefs[i] = el;
    },
    run(action) {
      if (!action || action.disabled) return;
      this.close(false);
      if (typeof action.onClick === "function") action.onClick(action);
    },
    toggle() {
      // a click opens it without focusing an item (the focused first item looked "selected"); arrow keys still focus
      if (this.open) this.close(false);
      else this.openMenu(null);
    },
    openMenu(focusIndex) {
      this.open = true;
      this.addListeners();
      if (focusIndex != null) this.$nextTick(() => this.focusItem(focusIndex));
    },
    close(returnFocus) {
      if (!this.open) return;
      this.open = false;
      this.removeListeners();
      if (returnFocus) this.$nextTick(() => this.$refs.trigger?.focus());
    },
    enabledItems() {
      return this.itemRefs.filter(el => el && !el.disabled);
    },
    focusItem(index) {
      const items = this.enabledItems();
      if (!items.length) return;
      const i = ((index % items.length) + items.length) % items.length;
      items[i].focus();
    },
    onMenuKeydown(e) {
      const items = this.enabledItems();
      const current = items.indexOf(document.activeElement);
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.focusItem(current + 1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.focusItem(current < 0 ? -1 : current - 1);
      } else if (e.key === "Home") {
        e.preventDefault();
        this.focusItem(0);
      } else if (e.key === "End") {
        e.preventDefault();
        this.focusItem(-1);
      } else if (e.key === "Escape") {
        e.preventDefault();
        this.close(true);
      } else if (e.key === "Tab") {
        this.close(false);
      }
    },
    onDocPointer(e) {
      if (this.$refs.root && !this.$refs.root.contains(e.target)) this.close(false);
    },
    onDocKeydown(e) {
      // Escape while focus is on the trigger (menu handles its own)
      if (e.key === "Escape" && this.$refs.root?.contains(document.activeElement)) this.close(true);
    },
    addListeners() {
      document.addEventListener("pointerdown", this.onDocPointer, true);
      document.addEventListener("keydown", this.onDocKeydown);
    },
    removeListeners() {
      document.removeEventListener("pointerdown", this.onDocPointer, true);
      document.removeEventListener("keydown", this.onDocKeydown);
    },
  },
};
</script>
